import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MATRIX_CHECKS, buildMatrix, qualityAlerts } from '../../utils/workspacePageModel';
import RunConfirm from './RunConfirm';
import DataTable from '../table/DataTable';
import QualityRunStatus, { RUN_DETAIL } from './QualityRunStatus';
import useQualityRuns, { qualityPlan } from './useQualityRuns';

// Red first: the rows that block Publish (most first), then those with any failing cell, then the rest as they were.
const badCells = (row) => (row.status === 'ready' ? Object.values(row.cells).filter((cell) => cell?.tone === 'bad').length : 0);
export const redFirst = (rows) => rows
  .map((row, index) => ({ row, index }))
  .sort((a, b) => ((b.row.publish?.blocked || 0) - (a.row.publish?.blocked || 0)) || (badCells(b.row) - badCells(a.row)) || a.index - b.index)
  .map(({ row }) => row);

// The matrix sorts a check's column on its colour: red, orange, green.
const TONE_ORDER = { bad: 0, warn: 1, ok: 2 };
/** Fix: Quality on the first check that fails (`&check=<id>`), else its overview. */
const fixParams = (row) => {
  const failing = Object.entries(row.cells || {}).find(([, cell]) => cell?.tone === 'bad');
  return failing ? { check: failing[0] } : null;
};

const cellChip = (cell) => (cell ? <span className={`ws-chip is-${cell.tone}`}>{cell.text}</span> : <span aria-label="No answer" className="ws-muted">—</span>);

/**
 * The workspace's Quality tab (design "Flow Workspace", Quality and alerts): a matrix of the projects against Tests,
 * Lint, Secrets, Vulnerabilities and Licences with a Publish column ("Ready" or "Blocked by n") and a Fix button that
 * opens the project's own Quality tab, and the Alerts list (failed build, serious vulnerability, secret still to set).
 * The projects are read one by one (a few at a time): a cell says "Loading" until its project answers, and a project
 * the sandbox cannot answer for says so instead of an invented value. Run all checks (asks first) starts the quality
 * run of each project the person may run and shows how far they are; the matrix reloads when they end. There is no
 * "Last 7 days" filter: the matrix reads each project's last run only, not a history by date.
 */
export default function WorkspaceQuality({ workspace, quality, secrets, runStates, onOpenProject, onReload }) {
  const { projects } = workspace;
  const rows = redFirst(buildMatrix(projects, quality, secrets));
  const checks = useQualityRuns({ onFinished: onReload });
  const [pending, setPending] = useState(null);
  const confirm = () => {
    const { plan } = pending;
    setPending(null);
    checks.clear();
    checks.start(plan.eligible);
  };
  const alerts = qualityAlerts(projects, { answers: runStates, quality, secrets });

  return (
    <div className="ws-quality">
      <div className="ws-tools">
        <strong className="ws-grow">Quality and alerts across the workspace</strong>
        <button className="ws-btn" onClick={onReload} type="button">Refresh</button>
        <button
          className="ws-btn is-primary"
          disabled={checks.active || projects.length === 0}
          onClick={() => setPending({ action: 'quality', plan: qualityPlan(projects), detail: RUN_DETAIL })}
          type="button"
        >{checks.active ? 'Running checks…' : 'Run all checks'}</button>
      </div>
      <QualityRunStatus checks={checks} />
      {projects.length === 0 ? <p className="workspaces-note">No projects in this workspace yet.</p> : (
        <DataTable
          ariaLabel="Quality of the projects"
          className="ws-table ws-matrix"
          columns={[
            {
              key: 'name',
              label: 'Project',
              value: (row) => row.name,
              title: (row) => row.name,
              render: (row) => <><button className="ws-name" data-testid="ws-quality-project-name" onClick={() => onOpenProject(row.projectId)} type="button"><strong>{row.name}</strong></button>{checks.runs[row.projectId]?.status === 'running' && <> <span className="ws-chip is-info">Checking…</span></>}</>,
            },
            ...MATRIX_CHECKS.map((check) => ({
              key: check.id,
              label: check.label,
              width: 120,
              align: 'center',
              className: 'ws-center',
              headerClassName: 'ws-center',
              // Red first, then orange, then green; a project without an answer last.
              value: (row) => (row.status === 'ready' && row.cells[check.id] ? TONE_ORDER[row.cells[check.id].tone] ?? 3 : null),
              title: (row) => (row.status === 'ready' ? row.cells[check.id]?.text : undefined),
              render: (row) => (
                <>
                  {row.status === 'loading' && <span className="ws-chip">Loading</span>}
                  {row.status === 'error' && <span aria-label="Not available" className="ws-muted">—</span>}
                  {row.status === 'ready' && cellChip(row.cells[check.id])}
                </>
              ),
            })),
            {
              key: 'publish',
              label: 'Publish',
              width: 190,
              value: (row) => (row.status === 'ready' && row.publish ? -(row.publish.blocked || 0) : null),
              title: (row) => (row.status === 'ready' ? row.publish?.text : undefined),
              render: (row) => (
                <>
                  {row.status === 'loading' && <span className="ws-chip">Loading</span>}
                  {row.status === 'error' && <span className="ws-muted">Not available for this project</span>}
                  {row.status === 'ready' && (row.publish ? <span className={`ws-chip is-${row.publish.tone}`}>{row.publish.text}</span> : cellChip(null))}
                  {row.status === 'ready' && row.publish?.blocked > 0 && (
                    <button aria-label={`Fix ${row.name}`} className="ws-btn ws-fix" onClick={() => onOpenProject(row.projectId, { tab: 'quality', params: fixParams(row) })} type="button">Fix</button>
                  )}
                </>
              ),
            },
          ]}
          rowKey="projectId"
          rows={rows}
          scrollLabel="Quality matrix, scrolls sideways"
          storageKey="flow.workspaceQuality.hiddenColumns"
        />
      )}
      <section aria-label="Alerts" className="ws-card ws-alerts">
        <h2 className="ws-card-title">Alerts {alerts.length > 0 && <span className="ws-chip is-bad">{alerts.length}</span>}</h2>
        {alerts.length === 0 ? <p className="ws-muted">No alerts: no failed build, no serious vulnerability, no secret left to set.</p> : (
          <ul className="ws-rows">
            {alerts.map((alert) => (
              <li className="ws-row" key={alert.id}>
                <span className={`ws-chip is-${alert.tone}`}>{alert.label}</span>
                <span className="ws-grow">{alert.text}</span>
                <button
                  aria-label={`${alert.button}: ${alert.text}`}
                  className="ws-btn"
                  onClick={() => onOpenProject(alert.projectId, alert.action === 'log' ? { tab: 'logs' } : { tab: alert.action, params: alert.params || null })}
                  type="button"
                >{alert.button}</button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <RunConfirm onCancel={() => setPending(null)} onConfirm={confirm} pending={pending} />
    </div>
  );
}

WorkspaceQuality.propTypes = {
  onOpenProject: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  quality: PropTypes.object.isRequired,
  runStates: PropTypes.object.isRequired,
  secrets: PropTypes.object.isRequired,
  workspace: PropTypes.object.isRequired,
};
